import { FC } from 'react'
import { SettingsPageLayout } from '../components/SettingsPageLayout'
import { useSearchSettings } from 'SettingsV2/hooks/useSearchSettings'
import {
  EmptyState,
  SettingsFormControl,
  SettingsGroup,
  SettingsGroupSearch,
  Switch,
  highlightQueryMatch,
  IconBanknote,
  Box,
  Flex
} from '@sequencehq/core-components'
import { Currency } from '@sequencehq/api'
import { useCurrencySettings } from 'SettingsV2/components/currencies/useCurrencySettings'
import { currencyToName } from '@sequencehq/utils'

export const CurrenciesSettingsPage: FC = () => {
  const { enabledCurrencies, isError, isLoading, updateEnabledCurrency } =
    useCurrencySettings()

  const currencies = Object.entries(currencyToName).map(([code, name]) => ({
    code: code as Currency,
    name
  }))

  const { query, setQuery, results } = useSearchSettings(currencies, {
    keys: ['code', 'name']
  })

  const mainCurrencies = new Set(['EUR', 'GBP', 'USD'])
  const main = currencies.filter(currency => mainCurrencies.has(currency.code))
  const other = currencies.filter(
    currency => !mainCurrencies.has(currency.code)
  )

  return (
    <SettingsPageLayout
      header="Currencies"
      subheader="Configure and manage enabled currencies"
      isError={isError}
    >
      <Flex flexDirection="column" gap="24px">
        <SettingsGroupSearch
          styles={{ wrapper: { marginBottom: '0px' } }}
          placeholder="Search by currency name"
          value={query}
          onChange={el => setQuery(el.target.value)}
        />
        {query ? (
          <SettingsGroup rows={22}>
            {results.map(currency => (
              <SettingsFormControl
                key={currency.code}
                htmlFor={currency.code}
                label={highlightQueryMatch(
                  `${currency.code} - ${currency.name}`,
                  query
                )}
              >
                <Switch
                  id={currency.code}
                  isChecked={enabledCurrencies?.includes(currency.code)}
                  onChange={(value: boolean) => {
                    updateEnabledCurrency(currency.code, value)
                  }}
                  disabled={isLoading}
                  size="md"
                  as="div"
                />
              </SettingsFormControl>
            ))}

            {results.length == 0 && (
              <EmptyState
                title="No currency found"
                icon={IconBanknote}
                iconSize="sm"
                body={`Your search for "${query}" did not return any results.`}
              />
            )}
          </SettingsGroup>
        ) : (
          <>
            <Box marginBottom="8px">
              <SettingsGroup rows={4}>
                {main.map(currency => (
                  <SettingsFormControl
                    key={currency.code}
                    htmlFor={currency.code}
                    label={highlightQueryMatch(
                      `${currency.code} - ${currency.name}`,
                      query
                    )}
                  >
                    <Switch
                      id={currency.code}
                      isChecked={enabledCurrencies?.includes(currency.code)}
                      onChange={(value: boolean) => {
                        updateEnabledCurrency(currency.code, value)
                      }}
                      disabled={isLoading}
                      size="md"
                      as="div"
                    />
                  </SettingsFormControl>
                ))}
              </SettingsGroup>
            </Box>
            <SettingsGroup title="Other currencies" rows={22}>
              {other.map(currency => (
                <SettingsFormControl
                  key={currency.code}
                  htmlFor={currency.code}
                  label={highlightQueryMatch(
                    `${currency.code} - ${currency.name}`,
                    query
                  )}
                >
                  <Switch
                    id={currency.code}
                    isChecked={enabledCurrencies?.includes(currency.code)}
                    onChange={(value: boolean) => {
                      updateEnabledCurrency(currency.code, value)
                    }}
                    disabled={isLoading}
                    size="md"
                    as="div"
                  />
                </SettingsFormControl>
              ))}
            </SettingsGroup>
          </>
        )}
      </Flex>
    </SettingsPageLayout>
  )
}
